import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
  Stack,
  Grid,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import "./ManagementsClaims.scss";
import { useTranslation } from "react-i18next";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import {
  ManagementRequestInfo,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
} from "../../core/models/managements/managements.model";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import ManagementHeader from "../ManagementsUpdate/ManagementsUpdateHeader/ManagementsUpdateHeader";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ManagementsRequestState from "../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  getManagementsRequestTypeData,
  getRegisterTypeId,
  initialManagementRequestInfo,
} from "../../utils/components/managements/managements.util";
import {
  MANAGEMENTS_REGISTER_TYPE_CLAIMS,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT,
} from "../../core/constants/constants";
import {
  postCallCenter as postCallCenterFetch,
  postImportDocument,
} from "../../core/services/managements/managements.service";
import { formatDate } from "../../utils/dateUtils/formatDate.util";
import ManagementUploadFile from "../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ManagementsCancelProcess from "../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import { getRecipientId } from "../../utils/components/services/services.util";

const ManagementsClaims = () => {
  const { t } = useTranslation();
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);
  const [deliveryAddress, setDeliveryAddress] = useState<string>("");
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  const [managementRequestInfo, setManagementRequestInfo] =
    useState<ManagementRequestInfo>(initialManagementRequestInfo);
  const [step, setStep] = useState<number>(0);
  const [isClaimLoading, setIsClaimLoading] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
      descriptionExtra: "",
    });
  const [file, setFile] = useState<File | null>(null);
  const [claimDetail, setClaimDetail] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const checkButtonDisabled = useCallback(() => {
    if (
      selectedDeliveryAddress.idCups &&
      claimDetail &&
      deliveryAddress.length
    ) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [selectedDeliveryAddress.idCups, claimDetail, deliveryAddress.length]);

  useEffect(() => {
    checkButtonDisabled();
  }, [checkButtonDisabled]);

  useEffect(() => {
    if (claimDetail.trim() !== "") {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [claimDetail]);

  const handleSendClaim = () => {
    if (ownerData && userData) {
      postCallCenter(claimDetail, deliveryAddress, selectedDeliveryAddress);
    }
  };

  const postCallCenter = (
    incidenceText: string,
    deliveryAddress: string,
    ownerDataCupSelected: OwnerDataCups,
  ): void => {
    const { contractCode, envirorment, idContract } = ownerDataCupSelected;
    const managementMessage = `El usuario ha realizado una petición de reclamación.${
      incidenceText ? ` Detalle de la petición: ${incidenceText}` : ""
    }`;

    if (userData) {
      const IdTipoRegistro = getRegisterTypeId(
        envirorment,
        MANAGEMENTS_REGISTER_TYPE_CLAIMS,
      );

      const requestBody: PostCallCenterRequestBody = {
        TextoIncidencia: managementMessage,
        CodigosContratos: [+contractCode],
        IdsContratos: [+idContract],
        IdTipoRegistro,
        IdSubTipoReclamacion: +deliveryAddress,
        idUsuarioDestino: getRecipientId(IdTipoRegistro, +deliveryAddress),
        ClaveMedio: isAgent ? "09" : "12",
      };
      setIsClaimLoading(true);
      setStep(1);

      postCallCenterFetch(requestBody, userData.token)
        .then(([{ IdCallCenter, IdCarpetaIncidencias, Cups, EntornoBD }]) => ({
          IdCallCenter,
          IdCarpetaIncidencias,
          Cups,
          EntornoBD,
        }))
        .then(
          async ({ IdCallCenter, IdCarpetaIncidencias, Cups, EntornoBD }) => {
            if (IdCallCenter === 0) {
              throw new Error("One or more IdCallCenter values are 0");
            }
            const address = ownerData?.cups.find(
              (item) => item.codeCups === Cups,
            )?.address;

            file &&
              (await postImportDocument(
                IdCallCenter,
                IdCarpetaIncidencias,
                file,
                userData.token,
              ));
            setRequestState(MANAGEMENTS_STATE_PENDING);
            setRequestStateData(
              getManagementsRequestTypeData(
                MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT,
                MANAGEMENTS_STATE_PENDING,
                isAgent ?? false,
              ),
            );
            setManagementRequestInfo({
              registrarionDate: formatDate(new Date()),
              refferenceNumber: IdCallCenter.toString(),
              cupsInfo: {
                cupsCode: Cups,
                address: address ?? "-",
                environment: EntornoBD,
              },
            });
            setIsClaimLoading(false);
          },
        )
        .catch((error) => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
          setIsClaimLoading(false);
        });
    }
  };

  const reasons = [
    {
      id: selectedDeliveryAddress.envirorment === "Luz" ? "8" : "33",
      nombre: "Calidad Suministro",
    },
    {
      id: selectedDeliveryAddress.envirorment === "Luz" ? "10" : "35",
      nombre: "Disconformidad consumo facturado",
    },
    {
      id: selectedDeliveryAddress.envirorment === "Luz" ? "9" : "34",
      nombre: "Disconformidad precios facturado",
    },
    {
      id: selectedDeliveryAddress.envirorment === "Luz" ? "1" : "31",
      nombre: "Retraso en facturación",
    },
    {
      id: selectedDeliveryAddress.envirorment === "Luz" ? "16" : "40",
      nombre: "Otros",
    },
  ];

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      component="section"
      className="last-managements"
      paddingBottom={{ md: "5rem" }}
    >
      <ManagementHeader
        title={"Reclamaciones"}
        step={step}
        showStepper={true}
      />

      {step === 0 && (
        <Grid container justifyContent="center">
          <Grid item md={8} lg={7} flex={1}>
            <Stack
              bgcolor={{ md: "#fff" }}
              borderRadius="12px"
              boxShadow={{ md: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)" }}
              padding="3rem"
              marginY="2.5rem"
              gap="2rem"
            >
              <Stack width={{ xs: "100%", md: "50%" }}>
                <ConsumptionsManagementsDeliveryAddressComponent
                  ownerDataCups={ownerData?.cups || []}
                  ownerDataCupSelected={selectedDeliveryAddress}
                  setOwnerDataCupSelected={(
                    ownerDataCupSelected: OwnerDataCups,
                  ) => setSelectedDeliveryAddress(ownerDataCupSelected)}
                />
              </Stack>
              <FormControl
                sx={{
                  display: "flex",
                  gap: "1rem",
                  width: { xs: "100%", md: "50%" },
                }}
              >
                <FormLabel htmlFor="reason-for-claim">
                  <Typography fontWeight={600}>
                    {t("Gestiones.Motivo de la reclamación")}
                    <span style={{ color: "red" }}> *</span>
                  </Typography>
                </FormLabel>
                <Select
                  sx={{ minWidth: "10rem" }}
                  inputProps={{
                    id: "reason-for-claim",
                  }}
                  value={deliveryAddress}
                  onChange={(event: SelectChangeEvent<string>) =>
                    setDeliveryAddress(event.target.value)
                  }
                >
                  {reasons.map(({ id, nombre }) => (
                    <MenuItem key={id} value={id}>
                      {t(`Gestiones.${nombre}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                sx={{
                  display: "flex",
                  gap: "1rem",
                }}
              >
                <FormLabel htmlFor="claim-detail">
                  <Typography fontWeight={600}>
                    {t("Gestiones.Detalle su reclamación")}
                    <span style={{ color: "red" }}> *</span>
                  </Typography>
                </FormLabel>
                <TextField
                  multiline
                  rows={7}
                  value={claimDetail}
                  onChange={(e) => setClaimDetail(e.target.value)}
                ></TextField>
              </FormControl>
              <ManagementUploadFile
                title={t("Adjuntos")}
                multipleFiles={true}
                onFileUpload={(id, file) => setFile(file)}
                onFileRemove={(id) => {
                  setFile(null);
                }}
              />
              <FormControl
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: "1rem",
                  marginY: "1.5rem",
                }}
              >
                <ButtonElement
                  onClick={() => handleSendClaim()}
                  disabled={isButtonDisabled}
                >
                  {t("Enviar reclamación")}
                </ButtonElement>
                <ButtonElement type="button" secondary onClick={openModal}>
                  {t("Cancelar")}
                </ButtonElement>
              </FormControl>
            </Stack>
            <ManagementsCancelProcess
              open={isModalOpen}
              handleClose={closeModal}
            />
          </Grid>
        </Grid>
      )}
      {[1, 2].includes(step) && (
        <ManagementsRequestState
          state={requestState}
          isLoading={isClaimLoading}
          managementRequestInfo={managementRequestInfo}
          showManagementRequestInfo
          title={requestStateData.title}
          description={requestStateData.description}
          descriptionExtra={requestStateData.descriptionExtra}
          cupsInfo={managementRequestInfo.cupsInfo}
        />
      )}
    </Box>
  );
};

export default ManagementsClaims;
